import {
  ActionButtons,
  Button,
  Heading,
  MobileNavbar,
  Para,
  Stack,
  ValueOrDash,
} from 'ui-components';
import GrayBox from './GrayBox';
import { CompleteBookingFragment, Maybe, ServiceState } from 'drycut-graphql';
import { formatDateTime } from 'ui-format-utils';
import { BookingWaitingTime } from 'controlled-components';
import { Navigation } from 'navigation';
import { useNavigate } from 'react-router-dom';
import { useTimeout } from 'usehooks-ts';

type Props = {
  booking?: Maybe<CompleteBookingFragment>;
  manual?: boolean;
};

export function BookingDetails({ booking, manual = false }: Props): JSX.Element {
  const statesToShowWaitingTime = [ServiceState.Queued, ServiceState.Called];
  const { state } = booking?.serviceExecution || { state: ServiceState.Ended };
  const shouldShowWaitingTime = booking && statesToShowWaitingTime.includes(state);
  const navigate = useNavigate();
  const href = booking?.manual ? `/${booking.store.slug}` : Navigation.HOME_URL;

  // If it's a manual booking in the store we need to redirect the customer after 20 seconds
  // so the next customer doesn't see the booking details in case this page is left open.
  useTimeout(() => {
    if (manual) {
      navigate(href);
    }
  }, 20000);

  return (
    <Stack
      gap={7}
      className="h-full text-center font-aribau gap-9 lg:gap-7"
      id="main-booking-details"
    >
      <MobileNavbar title="Deine Buchung" />

      <Stack>
        <Heading variant="h2" className="hidden lg:block">
          Deine Buchung
        </Heading>

        <Para className="mt-2" pretty>
          Hier kannst Du Deine aktuelle Wartezeit in Echtzeit verfolgen.
        </Para>
      </Stack>

      <GrayBox>
        <Para size="2xl">Deine Wartezeit</Para>

        <Para size="2xl" weight="bold" className="align-center">
          {shouldShowWaitingTime ? (
            <BookingWaitingTime booking={booking} showZero approximate steps />
          ) : (
            <ValueOrDash value={null} className="w-full h-7 my-2" />
          )}
        </Para>
      </GrayBox>

      <Stack gap={2}>
        <Para>Wir erwarten Dich im</Para>

        <Para size="2xl" weight="bold">
          DRYCUT {booking?.store.name}
        </Para>
      </Stack>

      <Stack>
        <Para weight="bold" className="text-primary-600">
          Deine Buchungsinformationen:
        </Para>

        <GrayBox gap={1} className="text-xs mt-2 text-zinc-500">
          <p>Name: {booking?.customer.name}</p>
          <p>Buchungscode: {booking?.bookingCode}</p>
          <p>Datum: {formatDateTime(booking?.createdAt, { dateStyle: 'long' })}</p>
        </GrayBox>
      </Stack>

      <ActionButtons>
        <Button as="a" href={href} role="link">
          Schliessen
        </Button>
      </ActionButtons>
    </Stack>
  );
}
